// Shared date-range filter card reused across all individual reports.
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

const PRESETS = [
  { key: "today",       label: "Today" },
  { key: "yesterday",   label: "Yesterday" },
  { key: "last_week",   label: "Last 7 days" },
  { key: "last_month",  label: "Last 30 days" },
  { key: "last_year",   label: "Last 365 days" },
  { key: "all_time",    label: "All time" },
  { key: "custom",      label: "Custom range" },
];

// Iter-f14 · Forward-only presets for demand-outlook reports (Future Booking).
const FORWARD_PRESETS = [
  { key: "this_month",    label: "This Month" },
  { key: "next_month",    label: "Next Month" },
  { key: "next_3_months", label: "Next 3 Months" },
  { key: "this_year",     label: "This Year" },
  { key: "custom",        label: "Custom Range" },
];

const BALI_TZ = "Asia/Makassar";

export function todayBali() {
  return new Date().toLocaleDateString("en-CA", { timeZone: BALI_TZ });
}

// Bali-local day arithmetic. Anchors at Bali NOON (04:00 UTC) and reads the
// result back in Bali time, so the instant can never straddle the UTC day
// boundary. The previous version anchored at Bali MIDNIGHT — which is 16:00
// UTC on the PREVIOUS day — and then sliced `toISOString()`, so every
// relative preset landed one day early ("Yesterday" resolved to two days
// ago, "Last 7 days" started 7 days back, etc). Same contract as
// ReportingEngine.addDays.
function addDays(dateStr, days) {
  const d = new Date(`${dateStr}T12:00:00+08:00`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toLocaleDateString("en-CA", { timeZone: BALI_TZ });
}

// Month helpers operate on "YYYY-MM-DD" strings anchored to Bali time.
function monthStart(dateStr, monthOffset = 0) {
  const [y, m] = dateStr.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + monthOffset, 1, 12));
  return d.toLocaleDateString("en-CA", { timeZone: BALI_TZ });
}

function monthEnd(dateStr, monthOffset = 0) {
  const [y, m] = dateStr.split("-").map(Number);
  const d = new Date(Date.UTC(y, m + monthOffset, 0, 12));
  return d.toLocaleDateString("en-CA", { timeZone: BALI_TZ });
}

export function rangeForPreset(preset, customFrom, customTo) {
  const today = todayBali();
  switch (preset) {
    case "this_month":    return { date_from: monthStart(today, 0), date_to: monthEnd(today, 0) };
    case "next_month":    return { date_from: monthStart(today, 1), date_to: monthEnd(today, 1) };
    case "next_3_months": return { date_from: monthStart(today, 0), date_to: monthEnd(today, 2) };
    case "this_year":     return { date_from: `${today.slice(0, 4)}-01-01`, date_to: `${today.slice(0, 4)}-12-31` };
    case "today":      return { date_from: today, date_to: today };
    case "yesterday":  { const y = addDays(today, -1); return { date_from: y, date_to: y }; }
    case "last_week":  return { date_from: addDays(today, -6), date_to: today };
    case "last_month": return { date_from: addDays(today, -29), date_to: today };
    case "last_year":  return { date_from: addDays(today, -364), date_to: today };
    case "all_time":   return {};
    case "custom":     return { date_from: customFrom, date_to: customTo };
    default:           return { date_from: today, date_to: today };
  }
}

export default function DateRangeFilter({ preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, loading, onRefresh, includeAllTime = true, forwardOnly = false }) {
  const presets = forwardOnly
    ? FORWARD_PRESETS
    : (includeAllTime ? PRESETS : PRESETS.filter((p) => p.key !== "all_time"));
  return (
    <Card className="rounded-lg">
      <CardContent className="p-3 flex items-center gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Date range</span>
        <Select value={preset} onValueChange={setPreset}>
          <SelectTrigger className="w-48" data-testid="report-date-preset"><SelectValue /></SelectTrigger>
          <SelectContent>
            {presets.map((p) => (<SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>))}
          </SelectContent>
        </Select>
        {preset === "custom" && (
          <>
            <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="w-40" data-testid="report-custom-from" />
            <span className="text-xs text-muted-foreground">to</span>
            <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="w-40" data-testid="report-custom-to" />
          </>
        )}
        <Button size="sm" variant="outline" onClick={onRefresh} disabled={loading} className="gap-1 ml-auto" data-testid="report-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </CardContent>
    </Card>
  );
}

export function useDateRange(defaultPreset = "today") {
  const [preset, setPreset] = React.useState(defaultPreset);
  const [customFrom, setCustomFrom] = React.useState(todayBali());
  const [customTo, setCustomTo] = React.useState(todayBali());
  const range = React.useMemo(
    () => rangeForPreset(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );
  return { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range };
}
